import {
  Button,
  EmptyState,
  FilterSegments,
  PageHeader,
  PageTitle,
  RowActions,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  formatDate,
  plural,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { routes } from '@/routes'
import { useNetworks, type NetworkFilter } from './api'
import { clinicCountLabel, sharingLabel } from './labels'

/**
 * Redes de clínicas: o painel da Westhetics (docs/areas/administracao.md, "Redes"; RN-ADM-54,
 * RT-06). Arquétipo: lista de trabalho, só de root, sem depender de clínica ativa. Rede não tem
 * pendência: abre em Todas; a vazia (desativada) tem o recorte Vazias (P-ADM-82).
 */

const FILTERS: NetworkFilter[] = ['all', 'empty']

export function NetworksPage() {
  const navigate = useNavigate()

  const { filter, setFilter } = useStoredFilter('networks', FILTERS, 'all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounced(search.trim())
  const query = useNetworks({ filter, search: debouncedSearch || undefined, page })
  const counts = query.data?.meta.counts

  const options: FilterSegmentOption<NetworkFilter>[] = [
    { value: 'all', label: 'Todas', count: counts?.all },
    { value: 'empty', label: 'Vazias', count: counts?.empty },
  ]

  const networks = query.data?.data ?? []
  const total = counts?.all ?? 0
  const hasNoNetwork = !query.isPending && total === 0
  const showSearch = total > 20 || search !== ''

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Westhetics' }, { label: 'Redes' }]}
        actions={
          <Button size="sm" asChild>
            <Link to={routes.admin.newNetwork}>+ Nova rede</Link>
          </Button>
        }
      />
      <Page wide>
        <PageTitle
          title="Redes"
          subtitle={
            counts
              ? `${plural(total, 'rede')} · ${counts.empty > 0 ? plural(counts.empty, 'vazia') : 'nenhuma vazia'}`
              : 'Conjuntos de clínicas do mesmo dono. Servem para uma clínica ler os pacientes da outra; nada mais é compartilhado.'
          }
        />

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar as redes"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : hasNoNetwork ? (
          <EmptyState
            className="mt-6"
            title="Nenhuma rede ainda"
            description="Rede é um conjunto de clínicas do mesmo dono. Serve para uma clínica ler os pacientes da outra; procedimentos, prestadores e usuários continuam de cada clínica."
            action={
              <Button className="max-desk:min-h-touch" asChild>
                <Link to={routes.admin.newNetwork}>Criar a primeira rede</Link>
              </Button>
            }
          />
        ) : (
          <>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <FilterSegments
                label="Filtrar redes"
                options={options}
                value={filter}
                onChange={(value) => {
                  setFilter(value)
                  setPage(1)
                }}
              />
              {showSearch ? (
                <SearchInput
                  className="tablet:max-w-[300px]"
                  value={search}
                  onChange={(value) => {
                    setSearch(value)
                    setPage(1)
                  }}
                  placeholder="Buscar por nome"
                  aria-label="Buscar rede"
                />
              ) : null}
            </div>

            <div className="mt-4">
              {query.isPending ? (
                <TableSkeleton rows={4} columns={4} />
              ) : networks.length === 0 ? (
                <EmptyState
                  title={
                    debouncedSearch
                      ? `Nenhuma rede para "${debouncedSearch}"`
                      : 'Nenhuma rede vazia'
                  }
                  description={
                    debouncedSearch
                      ? 'A busca cobre o nome da rede, dentro do segmento escolhido.'
                      : 'Toda rede tem pelo menos uma clínica.'
                  }
                  action={
                    debouncedSearch ? (
                      <Button
                        className="max-desk:min-h-touch"
                        variant="outline"
                        onClick={() => setSearch('')}
                      >
                        Limpar busca
                      </Button>
                    ) : (
                      <Button
                        className="max-desk:min-h-touch"
                        variant="outline"
                        onClick={() => setFilter('all')}
                      >
                        Ver todas
                      </Button>
                    )
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHead>Rede</TableHead>
                      <TableHead numeric>Clínicas</TableHead>
                      <TableHead>Pacientes</TableHead>
                      <TableHead>Criada em</TableHead>
                      <TableHead aria-label="Ações" />
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {networks.map((network) => (
                      <TableRow
                        key={network.id}
                        onClick={() => navigate(routes.admin.network(network.id))}
                      >
                        <TableCell primary>{network.name}</TableCell>
                        <TableCell label="Clínicas" numeric muted={network.clinicCount === 0}>
                          {clinicCountLabel(network)}
                        </TableCell>
                        <TableCell label="Pacientes" muted={!network.sharesPatients}>
                          {sharingLabel(network.sharesPatients)}
                        </TableCell>
                        <TableCell label="Criada em" muted>
                          {formatDate(new Date(network.createdAt))}
                        </TableCell>
                        <TableCell>
                          <RowActions>
                            <Link to={routes.admin.network(network.id)}>abrir</Link>
                          </RowActions>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {query.data && networks.length > 0 ? (
                <ListFooter
                  meta={query.data.meta}
                  noun={['rede', 'redes']}
                  note="em ordem de nome"
                  onPage={setPage}
                />
              ) : null}
            </div>
          </>
        )}
      </Page>
    </>
  )
}
